import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import {
  ArrowLeft, Pencil, Trash2, Mail, Phone, MapPin, Calendar, IdCard,
  Briefcase, Lock, Save, X, FileText, Trophy, Upload, Download, AlertTriangle, Award,
  TrendingUp, Euro, Users as UsersIcon, Clock, Wrench, Package2,
  Folder, FolderOpen, ChevronDown, ChevronRight, Plus,
  PenLine, CheckCircle2,
  CalendarDays, GraduationCap,
} from 'lucide-react';
import { useAuth } from '../lib/auth';
import { staffApi, storesApi, brandsApi, usersApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { FolderDialog } from '../components/FolderDialog';
import StoreMultiSelector from '../components/StoreMultiSelector';
import SignaturePad from '../components/SignaturePad';
import StaffCalendarTab from '../components/StaffCalendarTab';
import StaffTrainingPanel from '../components/training/StaffTrainingPanel';

const ROLE_LABELS = {
  operations_manager: 'Operations Manager',
  hr_manager: 'HR Manager',
  area_manager: 'Area Manager',
  store_manager: 'Store Manager',
  barber_senior: 'Barbiere Senior',
  barber_junior: 'Barbiere Junior',
  apprentice: 'Apprendista',
  receptionist: 'Reception',
  other: 'Altro',
};
const STATUS_LABELS = {
  active: 'Attivo',
  on_leave: 'In permesso',
  sick: 'In malattia',
  maternity: 'Maternità',
  terminated: 'Cessato',
};
const STATUS_TONE = {
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  on_leave: 'bg-blue-50 text-blue-700 border-blue-200',
  sick: 'bg-amber-50 text-amber-700 border-amber-200',
  maternity: 'bg-pink-50 text-pink-700 border-pink-200',
  terminated: 'bg-slate-100 text-slate-500 border-slate-200',
};
const CONTRACT_LABELS = {
  ccnl: 'CCNL',
  p_iva: 'Partita IVA',
  apprendistato: 'Apprendistato',
  stagista: 'Stage',
  other: 'Altro',
};

function initials(staff) {
  return `${(staff?.first_name || '?')[0] || ''}${(staff?.last_name || '?')[0] || ''}`.toUpperCase();
}

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' });
}

function tenure(hireDate) {
  if (!hireDate) return null;
  const start = new Date(hireDate);
  if (isNaN(start.getTime())) return null;
  const now = new Date();
  const totalMonths = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
  if (totalMonths < 1) return 'Nuovo arrivo';
  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;
  if (years === 0) return `${months} mes${months === 1 ? 'e' : 'i'}`;
  if (months === 0) return `${years} ann${years === 1 ? 'o' : 'i'}`;
  return `${years} ann${years === 1 ? 'o' : 'i'} e ${months} mes${months === 1 ? 'e' : 'i'}`;
}

function daysToBirthday(birthDate) {
  if (!birthDate) return null;
  const today = new Date();
  const bd = new Date(birthDate);
  if (isNaN(bd.getTime())) return null;
  const next = new Date(today.getFullYear(), bd.getMonth(), bd.getDate());
  if (next < today) next.setFullYear(today.getFullYear() + 1);
  const diff = Math.ceil((next - today) / (1000 * 60 * 60 * 24));
  return diff;
}

/**
 * StaffMemberPage — usato sia da /staff/{id} (vista per OPS/AM/SM)
 * sia da /my-profile (proprio profilo: passa `selfMode={true}`).
 */
export default function StaffMemberPage({ selfMode = false }) {
  const { staffId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const backTo = location.state?.backTo || (selfMode ? null : '/staff');
  const [data, setData] = useState(null);
  const [stores, setStores] = useState([]);
  const [brands, setBrands] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const isOps = user?.role === 'operations_manager';
  const isHR = user?.role === 'hr_manager';
  const isAM = user?.role === 'area_manager';
  const isSM = user?.role === 'store_manager';
  // isSelf: la scheda visualizzata è la propria (link via user_id)
  const isSelf = data?.user_id && data.user_id === user?.user_id;
  const canEdit = isOps || isAM || isSM || isSelf;
  const canDelete = isOps;
  const showPrivate = isOps || isSelf;
  // OPS + HR Manager possono creare/rinominare/eliminare cartelle staff (globali).
  const canManageFolders = isOps || isHR;

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [res, st, br, us] = await Promise.all([
        selfMode ? staffApi.me() : staffApi.get(staffId),
        storesApi.list({ include_special: true }).catch(() => ({ data: [] })),
        brandsApi.list().catch(() => ({ data: [] })),
        // OPS può linkare/sganciare staff↔account utente; gli altri non vedono /users.
        (user?.role === 'operations_manager') ? usersApi.list().catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      ]);
      setData(res.data);
      setStores(Array.isArray(st.data) ? st.data : (st.data?.stores || []));
      setBrands(br.data || []);
      setUsers(us.data || []);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || 'Errore caricamento scheda';
      setLoadError({ status, detail });
      // Self-mode 404: niente toast/navigate, mostriamo empty state inline.
      if (selfMode && status === 404) {
        // no-op
      } else {
        toast.error(detail);
        if (backTo) navigate(backTo);
      }
    } finally {
      setLoading(false);
    }
  }, [selfMode, staffId, backTo, navigate, user?.role]);

  useEffect(() => { load(); }, [load]);

  const storeName = useMemo(() => {
    const s = stores.find(s => s.store_id === data?.store_id);
    return s ? (s.display_name || s.name) : '—';
  }, [stores, data]);
  const brandName = useMemo(() => {
    const b = brands.find(b => b.brand_id === data?.brand_id);
    return b?.name || '—';
  }, [brands, data]);

  if (loading) return <div className="p-8"><div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin" /></div>;
  // Self-mode senza scheda collegata: empty state amichevole.
  if (selfMode && loadError?.status === 404) {
    return (
      <div className="p-8 max-w-xl mx-auto" data-testid="my-profile-not-linked">
        <div className="bg-white border border-slate-200 rounded-sm p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-slate-100 mx-auto mb-4 flex items-center justify-center">
            <IdCard className="w-8 h-8 text-slate-400" />
          </div>
          <h1 className="font-heading text-xl font-bold text-slate-900">Nessuna scheda collegata</h1>
          <p className="text-slate-600 text-sm mt-2 leading-relaxed">
            Il tuo account non è ancora collegato a una scheda dipendente.<br />
            Contatta il tuo Operations Manager per richiedere l'attivazione.
          </p>
        </div>
      </div>
    );
  }
  if (!data) return null;

  const tenureLbl = tenure(data.hire_date);
  const bdayDays = daysToBirthday(data.birth_date);

  async function handleDelete() {
    try {
      await staffApi.remove(data.staff_id);
      toast.success('Dipendente cessato');
      navigate('/staff');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore cessazione');
    }
  }

  return (
    <div className="p-4 md:p-8 max-w-[1280px]" data-testid="staff-detail-page">
      {/* Top bar back + status */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        {backTo ? (
          <Link to={backTo} className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1.5">
            <ArrowLeft className="w-4 h-4" /> Indietro
          </Link>
        ) : <div />}
        <span className={`text-[10px] uppercase tracking-wide px-3 py-1 border rounded-sm ${STATUS_TONE[data.status] || STATUS_TONE.active}`}>
          {STATUS_LABELS[data.status] || data.status}
        </span>
      </div>

      {/* Hero header */}
      <div className="bg-white border border-slate-200 rounded-sm p-5 md:p-6 mb-5" data-testid="staff-hero">
        <div className="flex flex-col sm:flex-row sm:items-start gap-4">
          <div className="flex items-start gap-4 flex-1 min-w-0">
            {data.photo_url ? (
              <img src={data.photo_url} alt="" className="w-16 h-16 md:w-24 md:h-24 rounded-full object-cover bg-slate-100 shrink-0" />
            ) : (
              <div className="w-16 h-16 md:w-24 md:h-24 rounded-full bg-slate-900 text-white flex items-center justify-center font-bold text-xl md:text-2xl shrink-0">
                {initials(data)}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h1 className="font-heading text-xl sm:text-2xl md:text-3xl font-bold tracking-tight text-slate-900 break-words">{data.first_name} {data.last_name}</h1>
              <p className="text-slate-600 text-xs sm:text-sm md:text-base mt-0.5 break-words">
                {ROLE_LABELS[data.role_in_store] || data.role_in_store}
                {' · '}{brandName}{' · '}{storeName}
              </p>
              {tenureLbl && (
                <p className="text-xs text-slate-500 mt-1">In azienda da {tenureLbl}{data.hire_date ? ` (${fmtDate(data.hire_date)})` : ''}</p>
              )}
            </div>
          </div>
          {!editing && canEdit && (
            <div className="flex gap-2 sm:shrink-0 w-full sm:w-auto">
              <Button variant="outline" size="sm" onClick={() => setEditing(true)} className="gap-1.5 flex-1 sm:flex-initial" data-testid="staff-edit-btn">
                <Pencil className="w-3.5 h-3.5" /> Modifica
              </Button>
              {canDelete && (
                <Button variant="outline" size="sm" onClick={() => setConfirmDelete(true)} className="gap-1.5 text-red-600 border-red-200 hover:bg-red-50 flex-1 sm:flex-initial" data-testid="staff-delete-btn">
                  <Trash2 className="w-3.5 h-3.5" /> Cessa
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Highlight box (compleanno / anniversario imminente) */}
        {bdayDays !== null && bdayDays <= 30 && (
          <div className="mt-4 px-3 py-2 bg-amber-50 border border-amber-200 rounded-sm text-sm text-amber-900" data-testid="birthday-hint">
            🎂 Compleanno{bdayDays === 0 ? ' oggi!' : ` tra ${bdayDays} giorn${bdayDays === 1 ? 'o' : 'i'}`}
          </div>
        )}

        {/* Account collegato (fix 2026-05-29): mostra che la scheda è
            linkata a un account utente dell'app. Senza questo banner gli
            OPS che avevano associato Giulia HR all'anagrafica non vedevano
            il successo del collegamento. */}
        {data.linked_user && (
          <div
            className="mt-4 px-3 py-2.5 bg-violet-50 border border-violet-200 rounded-sm text-sm text-violet-900 flex items-start gap-2"
            data-testid="staff-linked-user-badge"
          >
            <Lock className="w-4 h-4 mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="font-semibold">
                Account utente collegato: <span className="font-normal">{data.linked_user.name || data.linked_user.email}</span>
              </p>
              <p className="text-[11px] text-violet-700 mt-0.5">
                Email: {data.linked_user.email} · Ruolo app: <span className="font-medium uppercase tracking-wider">{ROLE_LABELS[data.linked_user.role] || data.linked_user.role}</span>
                {data.linked_user.status === 'invited' && <span className="ml-2 text-amber-800">· Invito in attesa di accettazione</span>}
              </p>
            </div>
          </div>
        )}
      </div>

      {editing ? (
        <EditForm
          data={data}
          stores={stores}
          brands={brands}
          users={users}
          showPrivate={showPrivate}
          isOps={isOps}
          isSelf={isSelf}
          onCancel={() => setEditing(false)}
          onSaved={(updated) => { setData(updated); setEditing(false); toast.success('Modifiche salvate'); }}
        />
      ) : (
        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList className="rounded-none bg-transparent border-b border-slate-200 h-auto p-0 gap-0 w-full overflow-x-auto justify-start whitespace-nowrap">
            <TabsTrigger value="overview" className="rounded-none border-b-2 border-transparent data-[state=active]:border-black data-[state=active]:bg-transparent data-[state=active]:shadow-none px-5 py-2.5" data-testid="tab-overview">
              <IdCard className="w-4 h-4 mr-2" /> Profilo
            </TabsTrigger>
            <TabsTrigger value="documents" className="rounded-none border-b-2 border-transparent data-[state=active]:border-black data-[state=active]:bg-transparent data-[state=active]:shadow-none px-5 py-2.5" data-testid="tab-documents">
              <FileText className="w-4 h-4 mr-2" /> Documenti
            </TabsTrigger>
            <TabsTrigger value="daily" className="rounded-none border-b-2 border-transparent data-[state=active]:border-black data-[state=active]:bg-transparent data-[state=active]:shadow-none px-5 py-2.5" data-testid="tab-daily">
              <CalendarDays className="w-4 h-4 mr-2" /> Giornaliero
            </TabsTrigger>
            <TabsTrigger value="performance" className="rounded-none border-b-2 border-transparent data-[state=active]:border-black data-[state=active]:bg-transparent data-[state=active]:shadow-none px-5 py-2.5" data-testid="tab-performance">
              <TrendingUp className="w-4 h-4 mr-2" /> Performance
            </TabsTrigger>
            <TabsTrigger value="achievements" className="rounded-none border-b-2 border-transparent data-[state=active]:border-black data-[state=active]:bg-transparent data-[state=active]:shadow-none px-5 py-2.5" data-testid="tab-achievements">
              <Trophy className="w-4 h-4 mr-2" /> Achievement
            </TabsTrigger>
            <TabsTrigger value="training" className="rounded-none border-b-2 border-transparent data-[state=active]:border-black data-[state=active]:bg-transparent data-[state=active]:shadow-none px-5 py-2.5" data-testid="tab-training">
              <GraduationCap className="w-4 h-4 mr-2" /> Formazione
            </TabsTrigger>
            <TabsTrigger value="inventory" className="rounded-none border-b-2 border-transparent data-[state=active]:border-black data-[state=active]:bg-transparent data-[state=active]:shadow-none px-5 py-2.5" data-testid="tab-inventory">
              <Wrench className="w-4 h-4 mr-2" /> Strumenti
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <SectionCard title="Contatti" icon={Mail}>
                <Field label="Email aziendale" value={data.email_work} type="email" />
                <Field label="Telefono aziendale" value={data.phone_work} type="phone" />
                {showPrivate && (
                  <>
                    <Field label="Email personale" value={data.email_personal} type="email" private_ />
                    <Field label="Telefono personale" value={data.phone_personal} type="phone" private_ />
                    <Field label="Indirizzo" value={data.address} private_ multiline />
                  </>
                )}
              </SectionCard>

              <SectionCard title="Lavoro" icon={Briefcase}>
                <Field label="Negozio" value={storeName} />
                <Field label="Brand" value={brandName} />
                <Field label="Mansione" value={ROLE_LABELS[data.role_in_store] || data.role_in_store} />
                <Field label="Data assunzione" value={fmtDate(data.hire_date)} />
                {data.termination_date && <Field label="Data cessazione" value={fmtDate(data.termination_date)} />}
                {data.secondary_store_ids && data.secondary_store_ids.length > 0 && (
                  <Field label="Negozi secondari" value={data.secondary_store_ids.map(id => stores.find(s => s.store_id === id)?.display_name || id).join(', ')} />
                )}
              </SectionCard>

              {showPrivate && (
                <SectionCard title="Dati contrattuali (riservato)" icon={Lock} private_>
                  <Field label="Tipo contratto" value={CONTRACT_LABELS[data.contract_type] || data.contract_type} private_ />
                  <Field label="Livello CCNL" value={data.ccnl_level} private_ />
                  <Field label="Ore settimanali" value={data.weekly_hours ? `${data.weekly_hours} h/sett` : null} private_ />
                  <Field label="Codice fiscale" value={data.fiscal_code} private_ />
                  <Field label="IBAN" value={data.iban} private_ />
                </SectionCard>
              )}

              <SectionCard title="Anagrafica" icon={IdCard}>
                <Field label="Data di nascita" value={fmtDate(data.birth_date)} />
              </SectionCard>

              {data.notes_public && (
                <SectionCard title="Note" icon={FileText} className="lg:col-span-2">
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{data.notes_public}</p>
                </SectionCard>
              )}
              {showPrivate && data.notes_private && (
                <SectionCard title="Note riservate (OPS)" icon={Lock} private_ className="lg:col-span-2">
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{data.notes_private}</p>
                </SectionCard>
              )}
            </div>
            {!showPrivate && (
              <div className="mt-4 px-4 py-3 bg-slate-50 border border-slate-200 rounded-sm text-xs text-slate-500" data-testid="private-fields-hint">
                <Lock className="w-3.5 h-3.5 inline mr-1.5 align-text-bottom" />
                I dati sensibili (codice fiscale, IBAN, contratto, ecc.) sono visibili solo all'Operations Manager e al dipendente stesso.
              </div>
            )}
          </TabsContent>

          <TabsContent value="documents">
            <DocumentsPanel staffId={data.staff_id} canUpload={canEdit} isOps={isOps} isHR={isHR} userId={user?.user_id} isSelf={data.user_id === user?.user_id} canManageFolders={canManageFolders} />
          </TabsContent>

          <TabsContent value="daily">
            <StaffCalendarTab staffId={data.staff_id} staffName={data.display_name || `${data.first_name || ''} ${data.last_name || ''}`.trim() || 'Dipendente'} />
          </TabsContent>

          <TabsContent value="performance">
            <PerformancePanel staffId={data.staff_id} staffName={`${data.first_name} ${data.last_name}`} />
          </TabsContent>

          <TabsContent value="achievements">
            <AchievementsPanel staffId={data.staff_id} canCreate={isOps || isAM || isSM} canDelete={isOps} userId={user?.user_id} staffName={`${data.first_name} ${data.last_name}`} />
          </TabsContent>

          <TabsContent value="training">
            <StaffTrainingPanel staffId={data.staff_id} />
          </TabsContent>

          <TabsContent value="inventory">
            <StaffInventoryPanel staffId={data.staff_id} staffName={`${data.first_name} ${data.last_name}`} />
          </TabsContent>
        </Tabs>
      )}

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cessazione dipendente</AlertDialogTitle>
            <AlertDialogDescription>
              Confermi la cessazione di <b>{data.first_name} {data.last_name}</b>? La scheda resterà accessibile in archivio (status = "Cessato") ma il dipendente non comparirà più nella lista principale.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Cessa dipendente</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SectionCard({ title, icon: Icon, children, className = '', private_ = false }) {
  return (
    <div
      className={`bg-white border ${private_ ? 'border-amber-200 bg-amber-50/30' : 'border-slate-200'} rounded-sm p-5 ${className}`}
      data-testid={`section-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
    >
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon className={`w-4 h-4 ${private_ ? 'text-amber-700' : 'text-slate-500'}`} />}
        <h3 className={`text-xs uppercase tracking-wide font-semibold ${private_ ? 'text-amber-800' : 'text-slate-500'}`}>{title}</h3>
      </div>
      <div className="space-y-2.5">{children}</div>
    </div>
  );
}

function Field({ label, value, type, multiline = false, private_ = false }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wide text-slate-400">{label}</p>
      {value ? (
        type === 'email' ? (
          <a href={`mailto:${value}`} className="text-sm text-slate-900 hover:underline break-all">{value}</a>
        ) : type === 'phone' ? (
          <a href={`tel:${value}`} className="text-sm text-slate-900 hover:underline">{value}</a>
        ) : (
          <p className={`text-sm text-slate-900 ${multiline ? 'whitespace-pre-wrap' : ''}`}>{value}</p>
        )
      ) : (
        <p className="text-sm text-slate-400">—</p>
      )}
    </div>
  );
}

function EditForm({ data, stores, brands, users = [], showPrivate, isOps, isSelf, onCancel, onSaved }) {
  const [form, setForm] = useState({ ...data });
  const [saving, setSaving] = useState(false);
  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  // Self-edit (non-OPS): solo whitelist limitato (vedi backend update_staff).
  const SELF_EDITABLE = new Set(['photo_url', 'phone_personal', 'email_personal', 'address', 'phone_work', 'email_work']);
  const canEditField = (key) => {
    if (isOps) return true;
    // AM / SM: tutto eccetto private fields (gestito dal backend, ma replichiamo UX)
    if (!isSelf) {
      const PRIVATE = new Set(['fiscal_code', 'iban', 'address', 'phone_personal', 'email_personal', 'ccnl_level', 'contract_type', 'weekly_hours', 'notes_private']);
      return !PRIVATE.has(key);
    }
    // Self
    return SELF_EDITABLE.has(key);
  };

  const handleStoreChange = (storeId) => {
    const st = stores.find(s => s.store_id === storeId);
    setForm(prev => ({
      ...prev,
      store_id: storeId,
      brand_id: st?.brand_id || prev.brand_id,
      // Il nuovo primario non può restare anche tra i secondari.
      secondary_store_ids: (prev.secondary_store_ids || []).filter(id => id !== storeId),
    }));
  };

  async function save() {
    setSaving(true);
    try {
      // Costruiamo payload solo con i campi che sono cambiati e che l'utente può editare
      const payload = {};
      for (const [k, v] of Object.entries(form)) {
        if (!canEditField(k) && k !== 'staff_id' && k !== 'first_name' && k !== 'last_name') continue;
        if (data[k] !== v) payload[k] = v;
      }
      // first_name / last_name / display_name: editabili da OPS/AM/SM ma non da self (per UX)
      if (!isOps && isSelf) {
        delete payload.first_name;
        delete payload.last_name;
        delete payload.role_in_store;
        delete payload.store_id;
        delete payload.brand_id;
        delete payload.status;
        delete payload.hire_date;
        delete payload.birth_date;
      }
      if (Object.keys(payload).length === 0) {
        toast.info('Nessuna modifica da salvare');
        onCancel();
        return;
      }
      const r = await staffApi.update(data.staff_id, payload);
      onSaved(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5 space-y-4" data-testid="staff-edit-form">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-900">Modifica scheda</h3>
        <Button variant="ghost" size="sm" onClick={onCancel} className="gap-1.5">
          <X className="w-4 h-4" /> Annulla
        </Button>
      </div>

      {isOps && (
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-3" data-testid="edit-user-link-block">
          <Label className="text-xs uppercase tracking-wide text-slate-600">Collegamento account utente</Label>
          <Select
            value={form.user_id || 'none'}
            onValueChange={(v) => update('user_id', v === 'none' ? null : v)}
          >
            <SelectTrigger className="mt-1.5" data-testid="edit-user-link"><SelectValue placeholder="Nessun collegamento" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Nessun collegamento</SelectItem>
              {users
                .filter(u => !u._linkedToOther || u.user_id === data.user_id)
                .map(u => (
                  <SelectItem key={u.user_id} value={u.user_id}>
                    {u.name || u.email} {u.role ? `· ${u.role.replace(/_/g, ' ')}` : ''}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-slate-500 mt-1.5">
            Linkando un account, il dipendente vedrà la propria scheda da <span className="font-mono">/my-profile</span>.
          </p>
        </div>
      )}

      {!isSelf && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <FormField label="Nome">
            <Input value={form.first_name || ''} onChange={(e) => update('first_name', e.target.value)} disabled={!canEditField('first_name')} data-testid="edit-first-name" />
          </FormField>
          <FormField label="Cognome">
            <Input value={form.last_name || ''} onChange={(e) => update('last_name', e.target.value)} disabled={!canEditField('first_name')} data-testid="edit-last-name" />
          </FormField>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <FormField label="URL foto profilo">
          <Input value={form.photo_url || ''} onChange={(e) => update('photo_url', e.target.value)} placeholder="https://…" disabled={!canEditField('photo_url')} data-testid="edit-photo-url" />
        </FormField>
        <FormField label="Stato">
          <Select value={form.status} onValueChange={(v) => update('status', v)} disabled={!canEditField('status')}>
            <SelectTrigger data-testid="edit-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(STATUS_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </FormField>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <FormField label="Mansione">
          <Select value={form.role_in_store} onValueChange={(v) => update('role_in_store', v)} disabled={!canEditField('role_in_store')}>
            <SelectTrigger data-testid="edit-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(ROLE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </FormField>
        <FormField label="Negozio">
          <Select value={form.store_id} onValueChange={handleStoreChange} disabled={!canEditField('store_id')}>
            <SelectTrigger data-testid="edit-store"><SelectValue /></SelectTrigger>
            <SelectContent>
              {stores.map(s => <SelectItem key={s.store_id} value={s.store_id}>{s.display_name || s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </FormField>
      </div>

      {(isOps || !isSelf) && stores.length > 1 && (
        <FormField label="Negozi aggiuntivi (rotazione multi-negozio)">
          <StoreMultiSelector
            stores={stores}
            brands={brands}
            value={form.secondary_store_ids || []}
            onChange={(ids) => update('secondary_store_ids', ids)}
            excludeStoreId={form.store_id}
            disabled={!canEditField('store_id')}
            testIdPrefix="edit-secondary-store"
            maxHeight="max-h-40"
            groupByBrand
            helperText="Il dipendente apparirà nelle liste di tutti i negozi assegnati. Le richieste ferie restano legate al negozio principale."
          />
        </FormField>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <FormField label="Data assunzione">
          <Input type="date" value={form.hire_date || ''} onChange={(e) => update('hire_date', e.target.value)} disabled={!canEditField('hire_date')} data-testid="edit-hire-date" />
        </FormField>
        <FormField label="Data di nascita">
          <Input type="date" value={form.birth_date || ''} onChange={(e) => update('birth_date', e.target.value)} disabled={!canEditField('birth_date')} data-testid="edit-birth-date" />
        </FormField>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <FormField label="Email aziendale">
          <Input value={form.email_work || ''} onChange={(e) => update('email_work', e.target.value)} disabled={!canEditField('email_work')} data-testid="edit-email-work" />
        </FormField>
        <FormField label="Telefono aziendale">
          <Input value={form.phone_work || ''} onChange={(e) => update('phone_work', e.target.value)} disabled={!canEditField('phone_work')} data-testid="edit-phone-work" />
        </FormField>
      </div>

      <FormField label="Note pubbliche">
        <Textarea value={form.notes_public || ''} onChange={(e) => update('notes_public', e.target.value)} disabled={!canEditField('notes_public')} rows={2} data-testid="edit-notes-public" />
      </FormField>

      {showPrivate && (
        <div className="pt-4 border-t border-amber-200">
          <p className="text-xs uppercase tracking-wide text-amber-700 font-semibold mb-3 flex items-center gap-1.5">
            <Lock className="w-3.5 h-3.5" /> Dati riservati (visibili solo a te)
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FormField label="Email personale">
              <Input value={form.email_personal || ''} onChange={(e) => update('email_personal', e.target.value)} disabled={!canEditField('email_personal')} data-testid="edit-email-personal" />
            </FormField>
            <FormField label="Telefono personale">
              <Input value={form.phone_personal || ''} onChange={(e) => update('phone_personal', e.target.value)} disabled={!canEditField('phone_personal')} data-testid="edit-phone-personal" />
            </FormField>
          </div>
          <FormField label="Indirizzo">
            <Textarea value={form.address || ''} onChange={(e) => update('address', e.target.value)} disabled={!canEditField('address')} rows={2} data-testid="edit-address" />
          </FormField>
          {isOps && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <FormField label="Tipo contratto">
                  <Select value={form.contract_type || ''} onValueChange={(v) => update('contract_type', v)}>
                    <SelectTrigger data-testid="edit-contract-type"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(CONTRACT_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </FormField>
                <FormField label="Livello CCNL">
                  <Input value={form.ccnl_level || ''} onChange={(e) => update('ccnl_level', e.target.value)} placeholder="es. III livello" data-testid="edit-ccnl-level" />
                </FormField>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <FormField label="Ore settimanali">
                  <Input type="number" min="0" max="60" step="0.5" value={form.weekly_hours ?? ''} onChange={(e) => update('weekly_hours', e.target.value === '' ? null : parseFloat(e.target.value))} data-testid="edit-weekly-hours" />
                </FormField>
                <FormField label="Codice fiscale">
                  <Input value={form.fiscal_code || ''} onChange={(e) => update('fiscal_code', e.target.value.toUpperCase())} maxLength={16} data-testid="edit-fiscal-code" />
                </FormField>
              </div>
              <FormField label="IBAN">
                <Input value={form.iban || ''} onChange={(e) => update('iban', e.target.value.toUpperCase())} data-testid="edit-iban" />
              </FormField>
              <FormField label="Note riservate (visibili solo a OPS)">
                <Textarea value={form.notes_private || ''} onChange={(e) => update('notes_private', e.target.value)} rows={2} data-testid="edit-notes-private" />
              </FormField>
            </>
          )}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-3 border-t">
        <Button variant="outline" onClick={onCancel} disabled={saving}>Annulla</Button>
        <Button onClick={save} disabled={saving} className="gap-1.5" data-testid="staff-save-btn">
          <Save className="w-4 h-4" /> {saving ? 'Salvataggio…' : 'Salva modifiche'}
        </Button>
      </div>
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wide text-slate-500">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}


// ============================================================
// DocumentsPanel — tab Documenti
// ============================================================

const DOC_TYPE_LABELS = {
  contract: 'Contratto',
  haccp: 'Attestato HACCP',
  medical_visit: 'Visita medica',
  training: 'Formazione',
  id_document: 'Documento d\'identità',
  payslip: 'Busta paga',
  other: 'Altro',
};
const DOC_TYPE_TONE = {
  contract: 'bg-purple-50 text-purple-700 border-purple-200',
  haccp: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  medical_visit: 'bg-rose-50 text-rose-700 border-rose-200',
  training: 'bg-blue-50 text-blue-700 border-blue-200',
  id_document: 'bg-amber-50 text-amber-700 border-amber-200',
  payslip: 'bg-teal-50 text-teal-700 border-teal-200',
  other: 'bg-slate-50 text-slate-700 border-slate-200',
};

function expiryInfo(expires_at) {
  if (!expires_at) return null;
  const exp = new Date(expires_at);
  if (isNaN(exp.getTime())) return null;
  const today = new Date();
  const days = Math.ceil((exp - today) / (1000 * 60 * 60 * 24));
  if (days < 0) return { tone: 'bg-red-50 text-red-700 border-red-300', label: `Scaduto da ${Math.abs(days)} giorni`, urgent: true };
  if (days <= 30) return { tone: 'bg-amber-50 text-amber-700 border-amber-300', label: `In scadenza tra ${days} giorni`, urgent: true };
  return { tone: 'bg-slate-50 text-slate-600 border-slate-200', label: `Scade tra ${days} giorni`, urgent: false };
}

function DocumentsPanel({ staffId, canUpload, isOps, isHR, userId, isSelf, canManageFolders }) {
  const canRequestSig = isOps || isHR;
  const [docs, setDocs] = useState([]);
  const [folders, setFolders] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState(null); // null = "Tutti"
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [parentForNew, setParentForNew] = useState(null);
  const [renameTarget, setRenameTarget] = useState(null);
  const [confirmDelFolder, setConfirmDelFolder] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  // Firma documenti (2026-06-25)
  const [signTarget, setSignTarget] = useState(null);       // doc su cui aprire SignaturePad
  const [requestSigTarget, setRequestSigTarget] = useState(null); // doc per cui chiedere firma

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedFolderId) params.folder_id = selectedFolderId;
      const [docsResp, foldersResp] = await Promise.all([
        staffApi.listDocs(staffId, params),
        staffApi.listFolders().catch(() => ({ data: [] })),
      ]);
      setDocs(docsResp.data || []);
      setFolders(foldersResp.data || []);
    } catch (e) {
      toast.error('Errore caricamento documenti');
    } finally { setLoading(false); }
  }, [staffId, selectedFolderId]);

  useEffect(() => { load(); }, [load]);

  // Auto-espande la root anno corrente alla prima carica
  useEffect(() => {
    if (folders.length && expandedIds.size === 0) {
      const yr = folders.find(f => f.is_year_root);
      if (yr) setExpandedIds(new Set([yr.folder_id]));
    }
  }, [folders, expandedIds.size]);

  async function handleDelete() {
    if (!confirmDel) return;
    try {
      await staffApi.deleteDoc(staffId, confirmDel.doc_id);
      toast.success('Documento eliminato');
      setConfirmDel(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore eliminazione');
    }
  }

  async function handleDeleteFolder() {
    if (!confirmDelFolder) return;
    try {
      await staffApi.deleteFolder(confirmDelFolder.folder_id);
      toast.success('Cartella eliminata');
      setConfirmDelFolder(null);
      // Se era la cartella selezionata, torno a "Tutti"
      if (selectedFolderId === confirmDelFolder.folder_id) setSelectedFolderId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore eliminazione cartella');
    }
  }

  async function handleDownload(doc) {
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api${staffApi.downloadDocUrl(staffId, doc.doc_id)}`, {
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('storeops_session_token') || ''}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (e) {
      toast.error('Errore download documento');
    }
  }

  async function handleRequestSignature(doc, note) {
    try {
      await staffApi.requestSignature(staffId, doc.doc_id, { note: note || null });
      toast.success('Richiesta firma inviata al dipendente');
      setRequestSigTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore richiesta firma');
    }
  }

  async function handleSubmitSignature(b64) {
    if (!signTarget) return;
    try {
      await staffApi.signDoc(staffId, signTarget.doc_id, { signature_b64: b64 });
      toast.success('Documento firmato');
      setSignTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore firma documento');
    }
  }

  if (loading) return <div className="py-12"><div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin mx-auto" /></div>;

  // Documenti in scadenza in evidenza (≤30gg o scaduti)
  const urgent = docs.filter(d => d.expires_at && expiryInfo(d.expires_at)?.urgent);

  // Costruisce il tree: gruppo per parent_folder_id
  const tree = (() => {
    const byParent = {};
    folders.forEach(f => {
      const key = f.parent_folder_id || '__root__';
      if (!byParent[key]) byParent[key] = [];
      byParent[key].push(f);
    });
    Object.keys(byParent).forEach(k => {
      byParent[k].sort((a, b) => {
        if (a.is_year_root && b.is_year_root) return (b.year || 0) - (a.year || 0);
        return a.name.localeCompare(b.name, 'it');
      });
    });
    return byParent;
  })();

  function toggleExpand(folderId) {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });
  }

  function renderTree(parentKey) {
    const children = tree[parentKey] || [];
    if (!children.length) return null;
    return (
      <ul className="space-y-0.5">
        {children.map(f => {
          const isExpanded = expandedIds.has(f.folder_id);
          const hasKids = !!(tree[f.folder_id]?.length);
          const isSelected = selectedFolderId === f.folder_id;
          return (
            <li key={f.folder_id} className="select-none">
              <div className={`flex items-center gap-1 group rounded-sm pr-1 ${isSelected ? 'bg-slate-900 text-white' : 'hover:bg-slate-100'}`}>
                <button
                  type="button"
                  onClick={() => hasKids && toggleExpand(f.folder_id)}
                  className={`w-4 h-5 flex items-center justify-center ${hasKids ? '' : 'opacity-0 pointer-events-none'}`}
                  data-testid={`folder-toggle-${f.folder_id}`}
                >
                  {hasKids && (isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />)}
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedFolderId(f.folder_id)}
                  className="flex-1 text-left text-sm py-1 flex items-center gap-1.5 truncate"
                  data-testid={`folder-${f.folder_id}`}
                >
                  {isExpanded && hasKids ? <FolderOpen className="w-3.5 h-3.5 shrink-0" /> : <Folder className="w-3.5 h-3.5 shrink-0" />}
                  <span className="truncate">{f.name}</span>
                  {f.doc_count > 0 && (
                    <span className={`ml-auto text-[10px] px-1 rounded ${isSelected ? 'bg-slate-700' : 'bg-slate-200 text-slate-600'}`}>{f.doc_count}</span>
                  )}
                </button>
                {canManageFolders && !f.is_year_root && (
                  <div className="hidden group-hover:flex gap-0.5">
                    <button
                      type="button"
                      onClick={() => setRenameTarget(f)}
                      className={`p-1 rounded ${isSelected ? 'hover:bg-slate-700' : 'hover:bg-slate-200'}`}
                      title="Rinomina"
                      data-testid={`folder-rename-${f.folder_id}`}
                    ><Pencil className="w-3 h-3" /></button>
                    <button
                      type="button"
                      onClick={() => setConfirmDelFolder(f)}
                      className={`p-1 rounded ${isSelected ? 'hover:bg-red-700 text-red-300' : 'hover:bg-red-50 text-red-600'}`}
                      title="Elimina"
                      data-testid={`folder-delete-${f.folder_id}`}
                    ><Trash2 className="w-3 h-3" /></button>
                  </div>
                )}
                {canManageFolders && (
                  <button
                    type="button"
                    onClick={() => { setParentForNew(f); setShowNewFolder(true); }}
                    className={`p-1 rounded ${isSelected ? 'hover:bg-slate-700' : 'hover:bg-slate-200'}`}
                    title="Nuova sotto-cartella"
                    data-testid={`folder-add-child-${f.folder_id}`}
                  ><Plus className="w-3 h-3" /></button>
                )}
              </div>
              {isExpanded && hasKids && (
                <div className="ml-4 border-l border-slate-200">
                  {renderTree(f.folder_id)}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    );
  }

  // Conteggio doc "non classificati" (folder_id null)
  const unfiledCount = docs.filter(d => !d.folder_id).length;
  const allDocsCount = folders.reduce((sum, f) => sum + (f.doc_count || 0), 0) + unfiledCount;

  return (
    <div className="space-y-4" data-testid="documents-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-slate-500 text-sm">{docs.length} document{docs.length === 1 ? 'o' : 'i'} {selectedFolderId ? 'in questa cartella' : 'totali'}</p>
        {canUpload && (
          <Button onClick={() => setShowUpload(true)} size="sm" className="gap-1.5" data-testid="upload-doc-btn">
            <Upload className="w-3.5 h-3.5" /> Carica documento
          </Button>
        )}
      </div>

      {urgent.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-3" data-testid="docs-urgent-banner">
          <p className="text-xs font-semibold uppercase tracking-wide text-amber-800 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" /> {urgent.length} document{urgent.length === 1 ? 'o richiede' : 'i richiedono'} attenzione
          </p>
          <p className="text-xs text-amber-700 mt-0.5">Scaduti o in scadenza entro 30 giorni</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Sidebar cartelle */}
        <div className="md:col-span-1 bg-white border border-slate-200 rounded-sm p-2">
          <div className="flex items-center justify-between mb-2 px-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Cartelle</p>
            {canManageFolders && (
              <button
                type="button"
                onClick={() => { setParentForNew(null); setShowNewFolder(true); }}
                className="p-1 rounded hover:bg-slate-100"
                title="Nuova cartella root"
                data-testid="folder-add-root"
              ><Plus className="w-3.5 h-3.5" /></button>
            )}
          </div>
          <button
            type="button"
            onClick={() => setSelectedFolderId(null)}
            className={`w-full text-left text-sm py-1 px-1 flex items-center gap-1.5 rounded-sm ${selectedFolderId === null ? 'bg-slate-900 text-white' : 'hover:bg-slate-100'}`}
            data-testid="folder-all"
          >
            <FolderOpen className="w-3.5 h-3.5" /> Tutti
            <span className={`ml-auto text-[10px] px-1 rounded ${selectedFolderId === null ? 'bg-slate-700' : 'bg-slate-200 text-slate-600'}`}>{allDocsCount}</span>
          </button>
          {unfiledCount > 0 && (
            <button
              type="button"
              onClick={() => setSelectedFolderId('__unfiled__')}
              className={`w-full text-left text-sm py-1 px-1 flex items-center gap-1.5 rounded-sm ${selectedFolderId === '__unfiled__' ? 'bg-slate-900 text-white' : 'hover:bg-slate-100'}`}
              data-testid="folder-unfiled"
            >
              <Folder className="w-3.5 h-3.5" /> Non classificati
              <span className={`ml-auto text-[10px] px-1 rounded ${selectedFolderId === '__unfiled__' ? 'bg-slate-700' : 'bg-slate-200 text-slate-600'}`}>{unfiledCount}</span>
            </button>
          )}
          <div className="mt-1">
            {renderTree('__root__')}
          </div>
        </div>

        {/* Lista documenti */}
        <div className="md:col-span-3">
          {docs.length === 0 ? (
            <div className="bg-slate-50 border border-slate-200 rounded-sm p-12 text-center" data-testid="docs-empty">
              <FileText className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-600 font-medium">Nessun documento</p>
              <p className="text-slate-500 text-sm mt-1">{selectedFolderId ? 'Questa cartella è vuota.' : 'Carica contratti, attestati HACCP, visite mediche e altri documenti.'}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {docs.map(doc => {
                const exp = expiryInfo(doc.expires_at);
                const canRemove = isOps || doc.uploaded_by === userId;
                const folderName = doc.folder_id ? (folders.find(f => f.folder_id === doc.folder_id)?.path || '—') : null;
                return (
                  <div key={doc.doc_id} className="bg-white border border-slate-200 rounded-sm p-3 md:p-4 flex items-start gap-3 flex-wrap md:flex-nowrap" data-testid={`doc-${doc.doc_id}`}>
                    <FileText className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-slate-900 truncate">{doc.title}</p>
                        <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 border rounded-sm ${DOC_TYPE_TONE[doc.doc_type] || DOC_TYPE_TONE.other}`}>
                          {DOC_TYPE_LABELS[doc.doc_type] || doc.doc_type}
                        </span>
                        {doc.is_private && (
                          <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 border rounded-sm bg-amber-50 text-amber-700 border-amber-200 inline-flex items-center gap-1">
                            <Lock className="w-2.5 h-2.5" /> Riservato
                          </span>
                        )}
                        {doc.signed && (
                          <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 border rounded-sm bg-emerald-50 text-emerald-700 border-emerald-200 inline-flex items-center gap-1" data-testid={`doc-signed-badge-${doc.doc_id}`}>
                            <CheckCircle2 className="w-2.5 h-2.5" /> Firmato {doc.signed_at ? `· ${new Date(doc.signed_at).toLocaleDateString('it-IT')}` : ''}
                          </span>
                        )}
                        {!doc.signed && doc.requires_signature && (
                          <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 border rounded-sm bg-orange-50 text-orange-700 border-orange-200 inline-flex items-center gap-1" data-testid={`doc-tosign-badge-${doc.doc_id}`}>
                            <PenLine className="w-2.5 h-2.5" /> Da firmare
                          </span>
                        )}
                        {folderName && (
                          <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 border rounded-sm bg-slate-50 text-slate-600 border-slate-200 inline-flex items-center gap-1">
                            <Folder className="w-2.5 h-2.5" /> {folderName}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {doc.filename} · {(doc.size / 1024).toFixed(0)} KB · Caricato {new Date(doc.uploaded_at).toLocaleDateString('it-IT')}
                      </p>
                      {exp && (
                        <span className={`text-[11px] mt-1 inline-block px-2 py-0.5 border rounded-sm ${exp.tone}`}>
                          {exp.label}
                        </span>
                      )}
                      {doc.notes && <p className="text-xs text-slate-600 mt-1.5 whitespace-pre-wrap">{doc.notes}</p>}
                    </div>
                    <div className="flex gap-1 ml-auto flex-wrap">
                      <Button variant="ghost" size="sm" onClick={() => handleDownload(doc)} className="gap-1" data-testid={`download-doc-${doc.doc_id}`}>
                        <Download className="w-3.5 h-3.5" /> <span className="hidden md:inline">Apri</span>
                      </Button>
                      {canRequestSig && !doc.signed && !doc.requires_signature && (
                        <Button variant="ghost" size="sm" onClick={() => setRequestSigTarget(doc)} className="gap-1 text-orange-700 hover:bg-orange-50" data-testid={`request-sig-${doc.doc_id}`}>
                          <PenLine className="w-3.5 h-3.5" /> <span className="hidden md:inline">Richiedi firma</span>
                        </Button>
                      )}
                      {!doc.signed && doc.requires_signature && (isSelf || canRequestSig) && (
                        <Button variant="default" size="sm" onClick={() => setSignTarget(doc)} className="gap-1 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`sign-doc-${doc.doc_id}`}>
                          <PenLine className="w-3.5 h-3.5" /> Firma
                        </Button>
                      )}
                      {canRemove && (
                        <Button variant="ghost" size="sm" onClick={() => setConfirmDel(doc)} className="text-red-600 hover:bg-red-50 gap-1" data-testid={`delete-doc-${doc.doc_id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {showUpload && (
        <UploadDocDialog
          staffId={staffId}
          isOps={isOps}
          folders={folders}
          defaultFolderId={selectedFolderId && selectedFolderId !== '__unfiled__' ? selectedFolderId : null}
          onClose={() => setShowUpload(false)}
          onUploaded={() => { setShowUpload(false); load(); }}
        />
      )}

      {showNewFolder && (
        <FolderDialog
          mode="create"
          parent={parentForNew}
          onClose={() => { setShowNewFolder(false); setParentForNew(null); }}
          onSaved={() => { setShowNewFolder(false); setParentForNew(null); load(); }}
        />
      )}

      {renameTarget && (
        <FolderDialog
          mode="rename"
          folder={renameTarget}
          onClose={() => setRenameTarget(null)}
          onSaved={() => { setRenameTarget(null); load(); }}
        />
      )}

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Elimina documento</AlertDialogTitle>
            <AlertDialogDescription>
              Vuoi davvero eliminare <b>{confirmDel?.title}</b>? Il file verrà rimosso e non sarà più accessibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmDelFolder} onOpenChange={(o) => !o && setConfirmDelFolder(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Elimina cartella</AlertDialogTitle>
            <AlertDialogDescription>
              Vuoi davvero eliminare <b>{confirmDelFolder?.name}</b>? La cartella deve essere vuota — se contiene documenti o sotto-cartelle, l'eliminazione fallirà.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteFolder} className="bg-red-600 hover:bg-red-700">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {requestSigTarget && (
        <RequestSignatureDialog
          doc={requestSigTarget}
          onClose={() => setRequestSigTarget(null)}
          onSubmit={(note) => handleRequestSignature(requestSigTarget, note)}
        />
      )}

      {signTarget && (
        <SignaturePad
          title={signTarget.title || 'Documento'}
          subtitle="Firma con il dito o lo stilo. La firma verrà incollata sull'ultima pagina del documento."
          onCancel={() => setSignTarget(null)}
          onSubmit={handleSubmitSignature}
        />
      )}
    </div>
  );
}

function UploadDocDialog({ staffId, isOps, folders, defaultFolderId, onClose, onUploaded }) {
  const [file, setFile] = useState(null);
  const [docType, setDocType] = useState('contract');
  const [title, setTitle] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [notes, setNotes] = useState('');
  const [isPrivate, setIsPrivate] = useState('default');
  const [folderId, setFolderId] = useState(defaultFolderId || 'none');
  const [uploading, setUploading] = useState(false);

  async function submit() {
    if (!file) { toast.error('Seleziona un file'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('doc_type', docType);
      if (title) fd.append('title', title);
      if (expiresAt) fd.append('expires_at', expiresAt);
      if (notes) fd.append('notes', notes);
      if (isPrivate !== 'default') fd.append('is_private', isPrivate);
      if (folderId && folderId !== 'none') fd.append('folder_id', folderId);
      await staffApi.uploadDoc(staffId, fd);
      toast.success('Documento caricato');
      onUploaded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore upload');
    } finally { setUploading(false); }
  }

  // Folders ordinate per path (year root prima, poi sotto-cartelle in albero)
  const folderOptions = [...(folders || [])].sort((a, b) => (a.path || '').localeCompare(b.path || ''));

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="upload-doc-dialog">
        <DialogHeader>
          <DialogTitle>Carica documento</DialogTitle>
          <DialogDescription>Allega un file PDF o immagine al profilo dipendente.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs uppercase tracking-wide">File *</Label>
            <Input type="file" accept=".pdf,image/jpeg,image/png,image/heic,image/heif,image/webp" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="upload-file" />
            <p className="text-[11px] text-slate-500 mt-1">PDF, JPG, PNG, HEIC, WEBP — max 10 MB</p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide">Tipo documento *</Label>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger data-testid="upload-doc-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(DOC_TYPE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide">Cartella</Label>
            <Select value={folderId} onValueChange={setFolderId}>
              <SelectTrigger data-testid="upload-folder"><SelectValue placeholder="Nessuna" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Nessuna cartella —</SelectItem>
                {folderOptions.map(f => (
                  <SelectItem key={f.folder_id} value={f.folder_id}>{f.path}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide">Titolo</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={file?.name || 'Es. Contratto a tempo indeterminato 2024'} data-testid="upload-title" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide">Scadenza (opzionale)</Label>
            <Input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} data-testid="upload-expires-at" />
            <p className="text-[11px] text-slate-500 mt-1">Riceverai un avviso quando manca meno di 30 giorni alla scadenza.</p>
          </div>
          {isOps && (
            <div>
              <Label className="text-xs uppercase tracking-wide">Visibilità</Label>
              <Select value={isPrivate} onValueChange={setIsPrivate}>
                <SelectTrigger data-testid="upload-is-private"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Default per tipo documento</SelectItem>
                  <SelectItem value="false">Pubblica (AM/SM possono vederla)</SelectItem>
                  <SelectItem value="true">Riservata (solo OPS o il dipendente stesso)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs uppercase tracking-wide">Note (opzionale)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="upload-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={uploading}>Annulla</Button>
          <Button onClick={submit} disabled={uploading || !file} data-testid="upload-submit">{uploading ? 'Caricamento…' : 'Carica'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// AchievementsPanel — tab Achievements (manuali + automatici)
// ============================================================

const ACH_TYPE_LABELS = {
  anniversary: 'Anniversario',
  training_completed: 'Formazione',
  top_reviews: 'Top recensioni',
  employee_of_month: 'Dipendente del mese',
  client_milestone: 'Traguardo clienti',
  custom: 'Riconoscimento speciale',
};
const ACH_GRADIENT = {
  anniversary: 'from-amber-500 to-orange-500',
  training_completed: 'from-blue-500 to-indigo-500',
  top_reviews: 'from-yellow-400 to-amber-500',
  employee_of_month: 'from-violet-500 to-fuchsia-500',
  client_milestone: 'from-emerald-500 to-teal-500',
  custom: 'from-slate-700 to-slate-900',
};

function AchievementsPanel({ staffId, canCreate, canDelete, userId, staffName }) {
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await staffApi.listAch(staffId);
      setAchievements(r.data || []);
    } catch (e) {
      toast.error('Errore caricamento achievement');
    } finally { setLoading(false); }
  }, [staffId]);

  useEffect(() => { load(); }, [load]);

  async function handleDelete() {
    if (!confirmDel) return;
    try {
      await staffApi.deleteAch(staffId, confirmDel.ach_id);
      toast.success('Achievement eliminato');
      setConfirmDel(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore eliminazione');
    }
  }

  if (loading) return <div className="py-12"><div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-4" data-testid="achievements-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-slate-500 text-sm">
          {achievements.length} achievement{achievements.length === 1 ? '' : 's'} · gli anniversari sono calcolati automaticamente dalla data di assunzione
        </p>
        {canCreate && (
          <Button onClick={() => setShowCreate(true)} size="sm" className="gap-1.5" data-testid="create-ach-btn">
            <Award className="w-3.5 h-3.5" /> Assegna riconoscimento
          </Button>
        )}
      </div>

      {achievements.length === 0 ? (
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-12 text-center" data-testid="ach-empty">
          <Trophy className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-600 font-medium">Nessun achievement</p>
          <p className="text-slate-500 text-sm mt-1">Quando il dipendente raggiungerà 1 anno di anzianità, riceverà automaticamente il primo badge.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="achievements-grid">
          {achievements.map(a => (
            <div
              key={a.ach_id}
              className={`relative rounded-sm overflow-hidden bg-gradient-to-br ${ACH_GRADIENT[a.ach_type] || ACH_GRADIENT.custom} text-white p-4 shadow-sm`}
              data-testid={`achievement-${a.ach_id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <span className="text-3xl leading-none">{a.icon || '🏅'}</span>
                {a.is_auto && (
                  <span className="text-[10px] uppercase tracking-wide bg-white/20 px-2 py-0.5 rounded-sm backdrop-blur">Auto</span>
                )}
              </div>
              <p className="font-bold text-base mt-3 leading-tight">{a.title}</p>
              <p className="text-xs text-white/90 mt-1 leading-snug">{a.description}</p>
              <p className="text-[11px] text-white/70 mt-2 uppercase tracking-wide">{ACH_TYPE_LABELS[a.ach_type] || a.ach_type}</p>
              {!a.is_auto && (canDelete || a.created_by === userId) && (
                <button
                  onClick={() => setConfirmDel(a)}
                  className="absolute top-2 right-2 p-1 rounded-sm hover:bg-white/20 transition-colors"
                  data-testid={`delete-ach-${a.ach_id}`}
                  aria-label="Elimina"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateAchDialog
          staffId={staffId}
          staffName={staffName}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Elimina achievement</AlertDialogTitle>
            <AlertDialogDescription>
              Confermi l'eliminazione del riconoscimento <b>{confirmDel?.title}</b>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CreateAchDialog({ staffId, staffName, onClose, onCreated }) {
  const [achType, setAchType] = useState('top_reviews');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState('⭐');
  const [earnedAt, setEarnedAt] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);

  const ICON_OPTIONS = ['⭐', '🏆', '🥇', '🥈', '🥉', '🎯', '💯', '🔥', '👑', '💎', '🌟', '🏅'];

  async function submit() {
    if (!title.trim()) { toast.error('Titolo obbligatorio'); return; }
    setSaving(true);
    try {
      await staffApi.createAch(staffId, { ach_type: achType, title: title.trim(), description: description.trim() || null, icon, earned_at: earnedAt });
      toast.success('Achievement assegnato a ' + staffName);
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore creazione');
    } finally { setSaving(false); }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="create-ach-dialog">
        <DialogHeader>
          <DialogTitle>Assegna riconoscimento a {staffName}</DialogTitle>
          <DialogDescription>Crea un nuovo badge da mostrare nella scheda dipendente.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs uppercase tracking-wide">Tipo</Label>
            <Select value={achType} onValueChange={setAchType}>
              <SelectTrigger data-testid="ach-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(ACH_TYPE_LABELS).filter(([v]) => v !== 'anniversary').map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-slate-500 mt-1">Gli "Anniversari" sono assegnati automaticamente dal sistema, non manualmente.</p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide">Titolo *</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Es. Top recensioni Aprile 2026" data-testid="ach-title" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide">Descrizione (opzionale)</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder="Es. 15 recensioni 5 stelle su Google in un mese" data-testid="ach-description" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide">Icona</Label>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {ICON_OPTIONS.map(emoji => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => setIcon(emoji)}
                  className={`w-10 h-10 text-xl rounded-sm border ${icon === emoji ? 'border-black bg-slate-100' : 'border-slate-200 hover:border-slate-400'}`}
                  data-testid={`ach-icon-${emoji}`}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide">Data riconoscimento</Label>
            <Input type="date" value={earnedAt} onChange={(e) => setEarnedAt(e.target.value)} data-testid="ach-earned-at" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Annulla</Button>
          <Button onClick={submit} disabled={saving} data-testid="ach-submit">{saving ? 'Salvataggio…' : 'Assegna'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



// ============================================================
// PerformancePanel — tab Performance (Fresha import data)
// ============================================================

const MONTH_LABELS = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

const KEY_METRICS = [
  { key: 'total_sales', label: 'Vendite totali', icon: Euro, format: 'currency', tone: 'emerald' },
  { key: 'services_sold', label: 'Servizi venduti', icon: TrendingUp, format: 'int', tone: 'blue' },
  { key: 'products_sold', label: 'Prodotti venduti', icon: TrendingUp, format: 'int', tone: 'yellow' },
  { key: 'total_clients', label: 'Clienti totali', icon: UsersIcon, format: 'int', tone: 'violet' },
  { key: 'booked_hours', label: 'Ore prenotate', icon: Clock, format: 'hours', tone: 'amber' },
  { key: 'occupancy_pct', label: '% Occupazione', icon: TrendingUp, format: 'pct', tone: 'rose' },
];

const DETAIL_METRICS = [
  { key: 'services_revenue', label: 'Ricavi servizi', format: 'currency' },
  { key: 'products_revenue', label: 'Ricavi prodotti', format: 'currency' },
  { key: 'tips', label: 'Mance', format: 'currency' },
  { key: 'avg_service_value', label: 'Valore medio servizio', format: 'currency' },
  { key: 'upsell_total', label: 'Totale upselling', format: 'currency' },
  { key: 'upsell_pct', label: '% upselling', format: 'pct' },
  { key: 'appointments_total', label: 'Appuntamenti totali', format: 'int' },
  { key: 'appointments_cancelled', label: 'Appuntamenti annullati', format: 'int' },
  { key: 'no_show_pct', label: '% no-show', format: 'pct' },
  { key: 'scheduled_hours', label: 'Ore programmate', format: 'hours' },
  { key: 'available_hours', label: 'Ore disponibili', format: 'hours' },
  { key: 'avg_appt_value', label: 'Valore medio appuntamento', format: 'currency' },
  { key: 'new_clients', label: 'Nuovi clienti', format: 'int' },
  { key: 'returning_clients', label: 'Clienti abituali', format: 'int' },
  { key: 'walk_in_clients', label: 'Walk-in', format: 'int' },
  { key: 'rebooked_clients', label: 'Clienti riprenotati', format: 'int' },
  { key: 'rebook_pct', label: '% riprenotazione', format: 'pct' },
];

const TONE_GRADIENTS = {
  emerald: 'from-emerald-50 to-emerald-100 border-emerald-200 text-emerald-900',
  blue: 'from-blue-50 to-blue-100 border-blue-200 text-blue-900',
  violet: 'from-violet-50 to-violet-100 border-violet-200 text-violet-900',
  amber: 'from-amber-50 to-amber-100 border-amber-200 text-amber-900',
  rose: 'from-rose-50 to-rose-100 border-rose-200 text-rose-900',
  yellow: 'from-yellow-50 to-yellow-100 border-yellow-200 text-yellow-900',
};

function fmtValue(value, format) {
  if (value === null || value === undefined) return '—';
  switch (format) {
    case 'currency': return `€ ${Math.round(value).toLocaleString('it-IT')}`;
    case 'int': return Math.round(value).toLocaleString('it-IT');
    case 'pct': return `${value.toFixed(1)}%`;
    case 'hours': return `${value.toFixed(1)}h`;
    case 'rating': return `★ ${value.toFixed(2)}`;
    default: return String(value);
  }
}

function pctDelta(current, previous) {
  if (current == null || previous == null || previous === 0) return null;
  return ((current - previous) / Math.abs(previous)) * 100;
}

function PerformancePanel({ staffId, staffName }) {
  const [performances, setPerformances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIdx, setSelectedIdx] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await staffApi.getPerformance(staffId, { months: 12 });
      setPerformances(r.data || []);
    } catch (e) {
      toast.error('Errore caricamento performance');
    } finally { setLoading(false); }
  }, [staffId]);

  useEffect(() => { load(); }, [load]);

  const current = performances[selectedIdx];
  const previous = performances[selectedIdx + 1];

  if (loading) return <div className="py-12"><div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin mx-auto" /></div>;

  if (performances.length === 0) {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-sm p-12 text-center" data-testid="performance-empty">
        <TrendingUp className="w-12 h-12 mx-auto text-slate-300 mb-3" />
        <p className="text-slate-600 font-medium">Nessun dato performance</p>
        <p className="text-slate-500 text-sm mt-1 max-w-sm mx-auto">
          Importa il report Fresha "Performance Summary" dalla pagina Staff per visualizzare ricavi, servizi, occupazione e rating di {staffName}.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="performance-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-slate-500">
          Dati disponibili: <strong className="text-slate-900">{performances.length}</strong> mes{performances.length === 1 ? 'e' : 'i'}
          {current?.source === 'daily_aggregate' && (
            <span className="ml-2 inline-block text-[11px] font-medium text-sky-800 bg-sky-50 border border-sky-200 px-2 py-0.5" data-testid="daily-aggregate-badge">
              Mese calcolato da {current.days_count} giorn{current.days_count === 1 ? 'o' : 'i'} di import giornalieri
            </span>
          )}
        </p>
        <Select value={String(selectedIdx)} onValueChange={(v) => setSelectedIdx(parseInt(v, 10))}>
          <SelectTrigger className="w-56 h-9 text-sm" data-testid="period-selector">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {performances.map((p, i) => (
              <SelectItem key={p.performance_id} value={String(i)}>
                {MONTH_LABELS[p.month - 1]} {p.year}{i === 0 ? ' (più recente)' : ''}{p.source === 'daily_aggregate' ? ' · da giornalieri' : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {KEY_METRICS.map(m => {
          const Icon = m.icon;
          const val = current.metrics?.[m.key];
          const prev = previous?.metrics?.[m.key];
          const delta = pctDelta(val, prev);
          return (
            <div key={m.key} className={`bg-gradient-to-br ${TONE_GRADIENTS[m.tone]} border rounded-sm p-4`} data-testid={`metric-${m.key}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <Icon className="w-4 h-4 opacity-70" />
                {delta !== null && (
                  <span className={`text-[10px] font-bold ${delta >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                    {delta >= 0 ? '▲' : '▼'} {Math.abs(delta).toFixed(1)}%
                  </span>
                )}
              </div>
              <p className="text-xl md:text-2xl font-bold">{fmtValue(val, m.format)}</p>
              <p className="text-xs opacity-80 mt-0.5">{m.label}</p>
            </div>
          );
        })}
      </div>

      {performances.length >= 2 && (
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-3">Trend vendite ultimi {performances.length} mesi</p>
          <div className="flex items-end gap-1.5 h-24" data-testid="sales-trend-chart">
            {[...performances].reverse().map((p) => {
              const max = Math.max(...performances.map(x => x.metrics?.total_sales || 0), 1);
              const val = p.metrics?.total_sales || 0;
              const h = (val / max) * 100;
              const isSelected = p.performance_id === current.performance_id;
              return (
                <div key={p.performance_id} className="flex-1 flex flex-col items-center gap-1">
                  <div className="text-[10px] text-slate-500 font-mono">{Math.round(val / 1000)}k</div>
                  <div
                    className={`w-full rounded-sm transition-all ${isSelected ? 'bg-slate-900' : 'bg-slate-300'}`}
                    style={{ height: `${Math.max(h, 5)}%` }}
                  />
                  <div className="text-[10px] text-slate-500">{MONTH_LABELS[p.month - 1]}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <details className="bg-white border border-slate-200 rounded-sm">
        <summary className="cursor-pointer p-4 text-xs uppercase tracking-wide text-slate-500 font-semibold">
          Tutte le metriche del periodo
        </summary>
        <div className="border-t border-slate-200">
          <table className="w-full text-sm">
            <tbody>
              {DETAIL_METRICS.map((m, i) => (
                <tr key={m.key} className={i % 2 ? 'bg-slate-50' : ''}>
                  <td className="px-4 py-2 text-slate-600">{m.label}</td>
                  <td className="px-4 py-2 text-right font-mono text-slate-900">
                    {fmtValue(current.metrics?.[m.key], m.format)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>

      <p className="text-[11px] text-slate-400 text-center">
        Dati importati da Fresha · ultimo aggiornamento {new Date(current.updated_at || current.created_at).toLocaleString('it-IT')}
      </p>
    </div>
  );
}


// ============================================================================
//  STAFF INVENTORY PANEL — mini-magazzino personale
// ============================================================================

/**
 * Tab "Strumenti" della scheda staff: lista degli inventory items affidati a
 * questo dipendente con totale valore e link diretto a ciascun item.
 * Refactor 2026-05-29: complemento del fix "Affidato a" (assignees endpoint).
 *
 * Mostra:
 *   - Header con conteggio + valore totale acquisto
 *   - Card per ogni strumento (codice, modello, categoria, brand, store, postazione)
 *   - Empty state se nessun strumento affidato
 *
 * Tutti i ruoli che vedono la scheda (OPS/AM/HR/SM/self) vedono questo tab.
 * Read-only: l'assegnazione si modifica dal magazzino, non da qui.
 */
function StaffInventoryPanel({ staffId, staffName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    staffApi.getInventory(staffId)
      .then(res => { if (active) setData(res.data); })
      .catch(() => { if (active) setData({ items: [], total: 0, total_value: 0 }); })
      .finally(() => { if (active) setLoading(false); });
  }, [staffId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="staff-inventory-loading">
        <div className="w-6 h-6 border-2 border-black border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const items = data?.items || [];
  const total = data?.total || 0;
  const totalValue = data?.total_value || 0;

  if (items.length === 0) {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-sm p-10 text-center" data-testid="staff-inventory-empty">
        <Package2 className="w-10 h-10 mx-auto text-slate-300 mb-3" />
        <h3 className="text-sm font-semibold text-slate-700">Nessuno strumento affidato</h3>
        <p className="text-xs text-slate-500 mt-1.5 max-w-md mx-auto">
          {staffName} non ha ancora strumenti registrati nel magazzino come affidati.
          Puoi assegnarne uno dalla <Link to="/inventory" className="underline underline-offset-2 hover:text-slate-900">pagina Magazzino</Link>,
          aprendo l'item e selezionando il nome del dipendente dal campo "Affidato a".
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="staff-inventory-panel">
      <div className="flex items-center justify-between gap-4 pb-3 border-b border-slate-200">
        <div>
          <h3 className="font-heading text-base font-semibold text-slate-900 flex items-center gap-2">
            <Wrench className="w-4 h-4" /> Strumenti affidati
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {total} {total === 1 ? 'strumento' : 'strumenti'} affidati a {staffName}
          </p>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Valore totale</p>
          <p className="text-lg font-bold text-slate-900 font-mono tabular-nums" data-testid="staff-inventory-total-value">
            €&nbsp;{totalValue.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map(it => (
          <Link
            key={it.item_id}
            to={`/inventory/${it.item_id}`}
            className="block border border-slate-200 rounded-sm p-3.5 hover:border-slate-900 transition-colors group"
            data-testid={`staff-inventory-item-${it.code}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="font-mono text-xs text-slate-500 tracking-wider">{it.code}</p>
                <p className="text-sm font-semibold text-slate-900 mt-0.5 truncate group-hover:underline underline-offset-2">
                  {it.model || it.category_name || '—'}
                </p>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1.5 text-[11px] text-slate-500">
                  {it.category_name && (
                    <span className="inline-flex items-center gap-1">
                      <Package2 className="w-3 h-3" /> {it.category_name}
                    </span>
                  )}
                  {it.brand_name && <span>· {it.brand_name}</span>}
                </div>
                <div className="flex flex-wrap items-center gap-x-2 mt-1 text-[11px] text-slate-500">
                  {it.store_name && (
                    <span className="inline-flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {it.store_name}
                    </span>
                  )}
                  {it.position && <span>· {it.position}</span>}
                </div>
              </div>
              {it.purchase_price != null && (
                <span className="text-xs font-mono tabular-nums text-slate-700 shrink-0">
                  €&nbsp;{Number(it.purchase_price).toLocaleString('it-IT', { minimumFractionDigits: 0 })}
                </span>
              )}
            </div>
          </Link>
        ))}
      </div>

      <p className="text-[11px] text-slate-400 mt-2">
        L'assegnazione degli strumenti si gestisce dalla pagina Magazzino. Per togliere o spostare un'assegnazione,
        clicca su una card e modifica il campo "Affidato a".
      </p>
    </div>
  );
}

function RequestSignatureDialog({ doc, onClose, onSubmit }) {
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit() {
    setSubmitting(true);
    try { await onSubmit(note.trim()); }
    finally { setSubmitting(false); }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="request-sig-dialog">
        <DialogHeader>
          <DialogTitle>Richiedi firma · {doc?.title}</DialogTitle>
          <DialogDescription>
            Il dipendente riceverà una notifica push (se ha l'app installata) e una in-app. Potrà firmare aprendo la propria pagina <em>Profilo</em>.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <Label htmlFor="sig-note" className="text-xs uppercase tracking-wide text-slate-500">Nota opzionale</Label>
          <Textarea
            id="sig-note"
            placeholder="Es. Firma entro fine settimana. Grazie."
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            data-testid="sig-note-input"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting} data-testid="sig-req-cancel">Annulla</Button>
          <Button onClick={handleSubmit} disabled={submitting} className="bg-orange-600 hover:bg-orange-700" data-testid="sig-req-submit">
            {submitting ? 'Invio…' : 'Invia richiesta'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

